import React, { useEffect, useState } from 'react';
import DialingSuccessChart from 'components/charts/DialingSuccessChart';
import { Col, Row } from 'react-bootstrap';
import { fetchHVACallingTodaySuccess } from 'services/hvacallingmanagment.service';
import { useAppContext } from 'providers/AppProvider';
import { numberFormat } from 'helpers/utils';

interface DialingSuccessProps {
  title: string;
  value: number;
  campaign?: number;
}

interface IssueStats {
  [key: string]: number;
}

const baseDescriptions = ['answer', 'busy', 'noanswer', 'noline', 'lineissue'];

const DialingSuccess = ({ title, value, campaign }: DialingSuccessProps) => {
  const {
    config: { isDark },
    getThemeColor,
    addAlphaToHexColor
  } = useAppContext();

  // eslint-disable-next-line
  const descriptionMapping: { [key: string]: { type: string; bg: string | React.CSSProperties; bgdark: string | React.CSSProperties } } = {
    // eslint-disable-next-line
    answer: { type: 'Answer', bg: { backgroundColor: 'rgba(158, 235, 139, 1)' }, bgdark: { backgroundColor: 'rgba(40, 193, 3, 1)' } },
    // eslint-disable-next-line
    busy: { type: 'Busy', bg: { backgroundColor: getThemeColor('primary-lighter') }, bgdark: { backgroundColor: getThemeColor('primary-lighter') } },
    // eslint-disable-next-line
    noanswer: { type: 'No answer', bg: { backgroundColor: getThemeColor('primary-light') }, bgdark: { backgroundColor: getThemeColor('primary-light') } },
    // eslint-disable-next-line
    noline: { type: 'No line', bg: { backgroundColor: getThemeColor('primary') }, bgdark: { backgroundColor: addAlphaToHexColor(getThemeColor('primary-dark'), 0.8) } },
    // eslint-disable-next-line
    lineissue: { type: 'Line issue', bg: { backgroundColor: getThemeColor('primary-darker') }, bgdark: { backgroundColor: getThemeColor('primary-darker') } }
  };

  const [issueStats, setIssueStats] = useState(
    baseDescriptions.map(description => ({
      type: descriptionMapping[description].type,
      value: 0,
      bg: descriptionMapping[description].bg,
      bgdark: descriptionMapping[description].bgdark
    }))
  );

  const fetchData = async () => {
    try {
      const params = { campaign, interval: value };
      const data = await fetchHVACallingTodaySuccess(params);

      const initialStats: IssueStats = baseDescriptions.reduce(
        (acc, desc) => ({ ...acc, [desc]: 0 }),
        {} as IssueStats
      );

      data.forEach((item: { description: string; total_count: number }) => {
        if (item.description in initialStats) {
          initialStats[item.description] = item.total_count;
        }
      });

      const updatedStats = baseDescriptions.map(description => ({
        type: descriptionMapping[description].type,
        value: initialStats[description],
        bg: descriptionMapping[description].bg,
        bgdark: descriptionMapping[description].bgdark
      }));

      setIssueStats(updatedStats);
    } catch (error) {
      console.error('Failed to fetch HVA calling success:', error);
    }
  };

  useEffect(() => {
    fetchData();

    const intervalId = setInterval(fetchData, 10000); // Actualiza cada 10 segundos

    return () => clearInterval(intervalId); // Limpia el intervalo al desmontar el componente
  }, [campaign, value]);

  // Calcula la suma de los valores
  // eslint-disable-next-line
  const totalSum = issueStats.reduce((sum, issue) => sum + Number(issue.value), 0);

  const chartData = issueStats.map(issue => ({
    value: issue.value,
    name: issue.type
  }));

  return (
    <Row className="g-3 mb-3">
      <Col xs={6} md={6}>
        <h5 className="text-nowrap mb-3">{title}</h5>
        <div className="d-flex align-items-center justify-content-between">
          <p className="mb-0 fw-bold"> </p>
          <p className="mb-0 fs-10">
            Total calls:
            <span className="fw-bold fs-9">{' ' + numberFormat(totalSum)}</span>
          </p>
        </div>
        <hr className="bg-body-secondary mb-2 mt-1" />

        {issueStats.map(issue => (
          <div className="d-flex align-items-center mb-1" key={issue.type}>
            <span
              className="d-inline-block bullet-item me-2"
              style={
                isDark
                  ? typeof issue.bgdark === 'object'
                    ? (issue.bgdark as React.CSSProperties)
                    : {}
                  : typeof issue.bg === 'object'
                  ? (issue.bg as React.CSSProperties)
                  : {}
              }
            />
            <p className="mb-0 fs-9 text-body lh-sm flex-1">{issue.type}</p>
            <h6 className="mb-0 text-body">{numberFormat(issue.value)}</h6>
          </div>
        ))}
      </Col>
      <Col xs={6} md={6}>
        <div className="position-relative mb-sm-4 mb-xl-0">
          <DialingSuccessChart data={chartData} />
        </div>
      </Col>
    </Row>
  );
};

export default DialingSuccess;
